import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Calendar, Clock, MapPin, Ticket, Users, UtensilsCrossed, Waves, AlertCircle, Loader2 } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { EmailSuggestion } from "../components/EmailSuggestion";
import { formatSwissPhone, isValidEmail } from "../lib/formHelpers";
import { SPECTACLES_RESERVABLES, reservationFor } from "../lib/spectacleReservations";
import { useChantiers } from "../lib/useChantiers";

const API = process.env.REACT_APP_BACKEND_URL;

// Aujourd'hui à Cugy (AAAA-MM-JJ), quel que soit le fuseau du téléphone.
const aujourdhuiZurich = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Zurich" }).format(new Date());

// Le spectacle a eu lieu : le serveur le dit (`closed`), sinon on compare les dates.
const spectaclePasse = (spectacle, places) =>
  Boolean(places?.closed) || (Boolean(spectacle.date_iso) && aujourdhuiZurich() > spectacle.date_iso);

export default function SpectacleReservation() {
  useSEO({
    title: "Réserver une place au spectacle de Noël 2026 — 1000 ARTS Cugy VD",
    description:
      "Réserve ta place pour le spectacle de Noël 2026 de 1000 ARTS : lundi 7 décembre 2026 à 19h00 à la Maison Villageoise de Cugy VD. Entrée libre, 250 places, don libre sur place reversé à Sea Shepherd Suisse.",
    canonical: "/inscription/spectacle",
  });
  const [params, setParams] = useSearchParams();
  const choisi = reservationFor(params.get("spectacle"));
  const chantiers = useChantiers();

  return (
    <div data-testid="page-spectacle-reservation" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
        {choisi ? (
          <button data-testid="spectacle-back" onClick={() => setParams({})} className="btn-ghost mb-6">
            <ArrowLeft size={16} /> Changer de spectacle
          </button>
        ) : (
          <Link to="/inscription" className="btn-ghost mb-6" data-testid="spectacle-back-inscription">
            <ArrowLeft size={16} /> Retour aux types d'inscription
          </Link>
        )}

        <div className="eyebrow mb-3">Réservation spectacle</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-tight mb-10">
          Réserve ta place<br />
          <span style={{ color: "var(--primary)" }}>au spectacle</span>
        </h1>

        {choisi ? (
          <ReservationForm spectacle={choisi} />
        ) : (
          <SelecteurSpectacle onSelect={(id) => setParams({ spectacle: id })} juin={chantiers.spectacles_juin} />
        )}
      </div>
    </div>
  );
}

/* Le choix du spectacle (Jen, 30.10.2026 : une seule entrée « Spectacle » dans Inscription) :
   Noël (entrée libre, une place réservée ici) et, là où la billetterie est ouverte (test
   d'abord), les spectacles de juin (billets payants, sur la page Billets). */
function SelecteurSpectacle({ onSelect, juin }) {
  return (
    <div className="space-y-5" data-testid="spectacle-selector">
      <div>
        <div className="eyebrow mb-2">Étape 2</div>
        <h2 className="font-display text-3xl tracking-tight">Quel spectacle ?</h2>
        <p className="text-[var(--muted)] mt-3">Les spectacles ouverts à la réservation apparaissent ici.</p>
      </div>
      {SPECTACLES_RESERVABLES.map((s) => (
        <button
          key={s.id}
          data-testid={`spectacle-choice-${s.id}`}
          onClick={() => onSelect(s.id)}
          className="card text-left w-full hover:border-[var(--primary)] transition-colors"
          style={{ cursor: "pointer" }}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
              <Ticket size={22} />
            </div>
            <div>
              <div className="font-display text-xl">{s.title}</div>
              <div className="text-sm text-[var(--muted)] mt-1">{s.subtitle}</div>
              <div className="text-sm mt-3">
                {s.date_label} · {s.time_label} · {s.location}
              </div>
              <div className="inline-flex items-center gap-1 text-sm font-medium mt-4" style={{ color: "var(--primary)" }}>
                Réserver une place <ArrowRight size={14} />
              </div>
            </div>
          </div>
        </button>
      ))}
      {/* Dans l'ordre du calendrier : Noël, puis juin. */}
      {juin && (
        <Link to="/spectacles/billets" data-testid="spectacle-vers-billets-juin"
          className="card block text-left w-full hover:border-[var(--primary)] transition-colors">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
              <Ticket size={22} />
            </div>
            <div>
              <div className="font-display text-xl">Spectacles de juin 2027</div>
              <div className="text-sm text-[var(--muted)] mt-1">Héros & Vilains (12 et 13 juin, Thierrens) · Le Monde de l'Autre (19 et 20 juin, Goumoens-la-Ville)</div>
              <div className="text-sm mt-3">Billets en ligne (TWINT) · plusieurs dates et personnes dans la même commande · et devenir bénévole</div>
              <div className="inline-flex items-center gap-1 text-sm font-medium mt-4" style={{ color: "var(--primary)" }}>
                Prendre mes billets <ArrowRight size={14} />
              </div>
            </div>
          </div>
        </Link>
      )}
    </div>
  );
}

function InfosSpectacle({ spectacle, places }) {
  const lignes = [
    { Icon: Calendar, label: "Date", value: spectacle.date_label },
    { Icon: Clock, label: "Horaire", value: `${spectacle.time_label} · ${spectacle.doors_label}` },
    { Icon: MapPin, label: "Lieu", value: spectacle.location },
    { Icon: UtensilsCrossed, label: "Sur place", value: `${spectacle.restauration} · entrée libre, don libre` },
  ];
  return (
    <section className="card-flat" data-testid="spectacle-infos">
      <div className="font-display text-2xl mb-1">{spectacle.title}</div>
      <div className="text-sm text-[var(--muted)] mb-5">{spectacle.subtitle}</div>
      <div className="grid md:grid-cols-2 gap-4">
        {lignes.map(({ Icon, label, value }) => (
          <div key={label} className="flex items-start gap-3">
            <Icon size={18} style={{ color: "var(--primary)" }} className="mt-1 shrink-0" />
            <div>
              <div className="eyebrow text-[10px]">{label}</div>
              <div className="text-sm font-medium">{value}</div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-sm text-[var(--muted)] leading-relaxed mt-5">{spectacle.intro}</p>

      <div
        className="mt-5 rounded-2xl p-4 flex items-start gap-3"
        style={{ background: "var(--primary-tint)", borderLeft: "4px solid var(--primary)" }}
        data-testid="spectacle-association"
      >
        <Waves size={20} style={{ color: "var(--primary)" }} className="mt-0.5 shrink-0" />
        <div className="text-sm leading-relaxed">
          L'intégralité des bénéfices de la soirée est reversée à{" "}
          <a href={spectacle.association.url} target="_blank" rel="noreferrer noopener" className="font-semibold underline" style={{ color: "var(--primary)" }}>
            {spectacle.association.name}
          </a>{" "}
          — pour la préservation des océans.
        </div>
      </div>

      <JaugePlaces spectacle={spectacle} places={places} />
    </section>
  );
}

function JaugePlaces({ spectacle, places }) {
  if (!places) {
    return (
      <div className="mt-5 text-sm text-[var(--muted)] flex items-center gap-2" data-testid="spectacle-places-loading">
        <Loader2 size={14} className="animate-spin" /> Calcul des places disponibles…
      </div>
    );
  }
  const pct = Math.min(100, Math.round((places.reserved / places.capacity) * 100));
  return (
    <div className="mt-6" data-testid="spectacle-places">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">
          {places.full ? (
            <span data-testid="spectacle-places-full">Salle complète — réservation en liste d'attente</span>
          ) : (
            <span data-testid="spectacle-places-remaining">
              {places.remaining} place{places.remaining > 1 ? "s" : ""} restante{places.remaining > 1 ? "s" : ""} sur {places.capacity}
            </span>
          )}
        </span>
        <span className="text-xs text-[var(--muted)]">{places.reserved} / {places.capacity} réservées</span>
      </div>
      <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "var(--border-soft)" }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: places.full ? "#DC2626" : "var(--primary)" }}
        />
      </div>
    </div>
  );
}

function ReservationForm({ spectacle }) {
  const [places, setPlaces] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", people: 2 });
  const [status, setStatus] = useState({ loading: false, msg: "" });
  const [done, setDone] = useState(null);

  const chargerPlaces = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/spectacles/${spectacle.id}/places`);
      if (res.ok) setPlaces(await res.json());
    } catch (e) {
      setPlaces({ capacity: spectacle.capacity, reserved: 0, remaining: spectacle.capacity, full: false });
    }
  }, [spectacle.id, spectacle.capacity]);

  useEffect(() => {
    chargerPlaces();
  }, [chargerPlaces]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setStatus({ loading: false, msg: "Merci d'indiquer ton prénom et ton nom." });
      return;
    }
    if (!isValidEmail(form.email)) {
      setStatus({ loading: false, msg: "Merci de vérifier ton adresse e-mail (ex : prenom.nom@exemple.ch)." });
      return;
    }
    setStatus({ loading: true, msg: "" });
    try {
      const res = await fetch(`${API}/api/inscriptions/spectacle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          spectacle_id: spectacle.id,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          people: Number(form.people),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erreur envoi");
      setDone(data);
      setStatus({ loading: false, msg: "" });
    } catch (err) {
      setStatus({ loading: false, msg: String(err.message || "Une erreur est survenue. Réessaye ou écris-nous à info1000arts@gmail.com.") });
    }
  };

  if (done) {
    return (
      <div className="space-y-6">
        <div className="card-flat text-center" data-testid="spectacle-success">
          <div className="text-5xl mb-4">{done.waitlist ? "🎟️" : "🎄"}</div>
          <h2 className="font-display text-2xl mb-3">
            {done.waitlist ? "Tu es sur la liste d'attente" : "Ta place est réservée !"}
          </h2>
          <p className="text-[var(--muted)] max-w-lg mx-auto">
            {done.waitlist
              ? `Toutes les places du ${spectacle.title} sont réservées. Ta demande pour ${form.people} personne${Number(form.people) > 1 ? "s" : ""} est enregistrée en liste d'attente — nous te contactons dès qu'une place se libère.`
              : `Merci ${form.first_name} ! Nous t'attendons ${spectacle.date_label.toLowerCase()} à ${spectacle.time_label} à ${spectacle.location}, avec ${form.people} personne${Number(form.people) > 1 ? "s" : ""}.`}
          </p>
          <div
            className="mt-6 mx-auto max-w-md p-5 rounded-2xl text-left"
            data-testid="spectacle-success-recap"
            style={{ background: "var(--primary-tint)", borderLeft: "4px solid var(--primary)" }}
          >
            <div className="eyebrow text-[10px] mb-2">Récapitulatif</div>
            <div className="font-display text-lg leading-tight">{spectacle.title}</div>
            <div className="text-sm mt-1">{spectacle.date_label} · {spectacle.time_label}</div>
            <div className="text-sm text-[var(--muted)]">{spectacle.doors_label} · {spectacle.location}</div>
            <div className="text-sm mt-2"><strong>{form.people} personne{Number(form.people) > 1 ? "s" : ""}</strong> · entrée libre</div>
          </div>
          <p className="text-xs text-[var(--muted)] mt-6 italic">Un e-mail de confirmation vient de t'être envoyé.</p>
        </div>
        <Link to="/spectacles" className="btn-ghost" data-testid="spectacle-success-back">
          <ArrowLeft size={16} /> Retour aux spectacles
        </Link>
      </div>
    );
  }

  const complet = places && places.full;
  // Plus de personnes que de places restantes : le serveur met la réservation en liste
  // d'attente ; on le dit avant l'envoi (01.11.2026 : le bouton disait « Réserver ma place »).
  const tropNombreux = Boolean(places && !places.full && Number(form.people) > places.remaining);

  if (spectaclePasse(spectacle, places)) {
    return (
      <div className="space-y-6">
        <div className="card-flat text-center" data-testid="spectacle-ferme">
          <div className="text-5xl mb-4">🎭</div>
          <h2 className="font-display text-2xl mb-3">Les réservations sont fermées</h2>
          <p className="text-[var(--muted)] max-w-lg mx-auto">
            Le {spectacle.title} a eu lieu le {spectacle.date_label.toLowerCase()}. Merci à toutes
            celles et ceux qui sont venus nous applaudir — rendez-vous au prochain spectacle&nbsp;!
          </p>
        </div>
        <Link to="/spectacles" className="btn-ghost" data-testid="spectacle-ferme-back">
          <ArrowLeft size={16} /> Retour aux spectacles
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InfosSpectacle spectacle={spectacle} places={places} />

      {/* noValidate : la validation est faite en JS pour afficher nos propres messages
          (la bulle native du navigateur court-circuiterait le handler sur type="email"). */}
      <form onSubmit={submit} noValidate className="space-y-6" data-testid="spectacle-form">
        {complet && (
          <div
            className="p-4 rounded-2xl text-sm flex items-start gap-2"
            style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}
            data-testid="spectacle-waitlist-notice"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            Les 250 places sont réservées. Tu peux quand même laisser tes coordonnées : ta demande sera
            enregistrée en <strong>liste d'attente</strong> et nous te contacterons dès qu'une place se libère.
          </div>
        )}

        <section className="card-flat">
          <div className="font-display text-xl mb-4">Qui réserve ?</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Prénom *</label>
              <input data-testid="spectacle-first-name" className="input-field" value={form.first_name} onChange={set("first_name")} required />
            </div>
            <div>
              <label className="field-label">Nom *</label>
              <input data-testid="spectacle-last-name" className="input-field" value={form.last_name} onChange={set("last_name")} required />
            </div>
            <div>
              <label className="field-label">E-mail *</label>
              {/* La validation est en JS (voir noValidate sur le formulaire) : on veut notre
                  message « vérifie ton adresse » et pas la bulle native du navigateur. */}
              <input data-testid="spectacle-email" type="email" className="input-field" value={form.email} onChange={set("email")} required />
              <EmailSuggestion value={form.email} onFix={(v) => setForm({ ...form, email: v })} testid="spectacle-email-suggestion" />
              <p className="text-xs text-[var(--muted)] mt-1">Pour recevoir la confirmation de ta réservation.</p>
            </div>
            <div>
              <label className="field-label">Téléphone <span className="text-[var(--muted)] font-normal">(facultatif)</span></label>
              <input
                data-testid="spectacle-phone"
                type="tel"
                inputMode="tel"
                className="input-field"
                placeholder="+41 79 950 40 31"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: formatSwissPhone(e.target.value) })}
              />
            </div>
          </div>
        </section>

        <section className="card-flat">
          <div className="flex items-center gap-2 mb-2">
            <Users size={20} style={{ color: "var(--primary)" }} />
            <div className="font-display text-xl">Combien de personnes viennent ?</div>
          </div>
          <p className="text-sm text-[var(--muted)] mb-4">
            Compte-toi aussi. Maximum {spectacle.max_per_reservation} personnes par réservation.
          </p>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: spectacle.max_per_reservation }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`spectacle-people-${n}`}
                onClick={() => setForm({ ...form, people: n })}
                className={`pill ${Number(form.people) === n ? "active" : ""}`}
                style={{ minWidth: 48, justifyContent: "center" }}
              >
                {n}
              </button>
            ))}
          </div>
        </section>

        {tropNombreux && (
          <div
            className="p-4 rounded-2xl text-sm flex items-start gap-2"
            style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}
            data-testid="spectacle-trop-nombreux"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>Il ne reste que <strong>{places.remaining} place{places.remaining > 1 ? "s" : ""}</strong> : pour {form.people} personnes,
              ta demande ira en <strong>liste d'attente</strong>. Pour une place confirmée, choisis {places.remaining} personne{places.remaining > 1 ? "s" : ""} au plus.</span>
          </div>
        )}

        {status.msg && (
          <div className="p-4 rounded-2xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2" data-testid="spectacle-error">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {status.msg}
          </div>
        )}

        <button type="submit" disabled={status.loading} data-testid="spectacle-submit" className="btn-primary w-full justify-center" style={{ padding: 16 }}>
          {status.loading ? "Envoi…" : complet || tropNombreux ? "M'inscrire en liste d'attente" : "Réserver ma place"} <ArrowRight size={16} />
        </button>
        <p className="text-xs text-[var(--muted)] text-center">
          Entrée libre — aucun paiement en ligne. Un don est possible sur place, reversé à {spectacle.association.name}.
        </p>
      </form>
    </div>
  );
}
